import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Users, Target, Heart, TrendingUp, Globe, 
  Zap, CheckCircle, ArrowRight, Building2
} from 'lucide-react';

function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { number: '10+', label: 'Años de experiencia' },
    { number: '5', label: 'Países en LATAM' },
    { number: '100+', label: 'Empresas activas' },
    { number: '24/7', label: 'Soporte dedicado' }
  ];

  const modules = [
    {
      category: 'CRM y Gestión Comercial',
      features: ['Gestión de leads y oportunidades', 'Embudos de venta configurables', 'Cotizaciones', 'Contratos y documentos', 'Workflows automatizados']
    },
    {
      category: 'Operaciones y Experiencia',
      features: ['Gestión de clientes y contactos', 'Espacios y salas de reunión', 'Reservas con Calendar/Outlook', 'Control de acceso', 'Parking inteligente', 'Muro de comunidad']
    },
    {
      category: 'Finanzas y Facturación',
      features: ['Facturación electrónica SII', 'Facturación recurrente', 'Suscripciones flexibles', 'Notificaciones automáticas', 'Inventario y órdenes de compra']
    },
    {
      category: 'Automatización y Hardware',
      features: ['Control de acceso con hardware', 'Gestión de impresión', 'Autenticación WiFi', 'Alertas de ocupación', 'App móvil personalizada']
    },
    {
      category: 'Integraciones',
      features: ['Zoho, Nubox, SII', 'APIs propias', 'Zapier', 'Conectividad total vía API']
    }
  ];

  const countries = [
    { name: 'Chile', flag: '🇨🇱' },
    { name: 'Perú', flag: '🇵🇪' },
    { name: 'Colombia', flag: '🇨🇴' },
    { name: 'Argentina', flag: '🇦🇷' },
    { name: 'Brasil', flag: '🇧🇷' }
  ];

  return (
    <>
      <SEOHead
        title="Quiénes Somos - Cloudbase | ERP Modular desde 2015 para LATAM"
        description="Conoce la historia de Cloudbase: ERP modular fundado en 2015 por Darko von Bischoffshausen. Más de 10 años desarrollando soluciones para empresas en Chile, Perú, Colombia, Argentina y Brasil."
        keywords="quiénes somos cloudbase, nosotros cloudbase, historia cloudbase, ERP LATAM, Darko von Bischoffshausen, Cloudbase Nexus, software empresarial Chile"
        path="/nosotros"
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Nosotros', item: '/nosotros' }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Quiénes Somos
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed font-light">
                Más de 10 años desarrollando tecnología que se adapta a tu negocio, no al revés.
              </p>
            </div>
          </div>
        </section>

        {/* Origin Story */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Nuestra Historia</h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Cloudbase es una empresa tecnológica fundada el <strong>2 de diciembre de 2015</strong> por <strong>Darko von Bischoffshausen</strong>, con el objetivo de desarrollar soluciones de software a medida que resolvieran los desafíos operacionales de empresas en crecimiento.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Con el tiempo, este trabajo evolucionó hacia la creación de un sistema propio: <strong>Cloudbase Nexus</strong>, un ERP modular y escalable diseñado para optimizar procesos, automatizar tareas y centralizar la gestión del negocio.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Desde nuestros inicios hemos sido una compañía enfocada en construir tecnología flexible, capaz de adaptarse a industrias dinámicas como <strong>coworking, servicios profesionales, tecnología, logística y empresas de suscripción</strong>, ofreciendo un ERP que combina simplicidad de uso con potencia funcional.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold text-green-600 mb-2">
                    {stat.number}
                  </div>
                  <p className="text-gray-600 text-lg">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Evolution */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Nuestra Evolución: De Desarrollos a Medida a ERP Integral
            </h2>
            
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Cloudbase comenzó como una empresa de desarrollos a medida, lo que nos permitió entender profundamente la operación real de nuestros clientes. Ese aprendizaje se transformó en la base de <strong>Nexus</strong>: un ERP integrado que unifica CRM, gestión comercial, operaciones, finanzas y automatización en una sola plataforma.
              </p>
            </div>

            <h3 className="text-3xl font-bold text-gray-900 mb-8">
              Nuestras Soluciones: Módulos Especializados
            </h3>

            <div className="space-y-8">
              {modules.map((module, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-lg">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">
                    {module.category}
                  </h4>
                  <ul className="space-y-3">
                    {module.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* LATAM Presence */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <Globe className="w-16 h-16 mx-auto mb-6" />
              <h2 className="text-4xl font-bold mb-6">
                Nuestra Presencia: Una Plataforma Pensada para LATAM
              </h2>
              <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Uno de los pilares de Cloudbase es entender las necesidades de las empresas latinoamericanas.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Legislación Local</h3>
                <p className="text-white/90">
                  Nuestro ERP considera la legislación tributaria local, como el SII en Chile, asegurando cumplimiento normativo.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Operación Flexible</h3>
                <p className="text-white/90">
                  Modelos adaptados para coworking, espacios compartidos y empresas de servicios regionales.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Procesos Regionales</h3>
                <p className="text-white/90">
                  Procesos de ventas y cobranzas adaptados a la realidad latinoamericana.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Integraciones Locales</h3>
                <p className="text-white/90">
                  Conexiones con sistemas ampliamente usados en toda Latinoamérica.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-center">
              {countries.map((country, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl mb-2">{country.flag}</div>
                  <p className="text-lg font-semibold">{country.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Purpose */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gray-50 p-10 rounded-lg">
                <Target className="w-12 h-12 text-green-600 mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Nuestra Misión
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed">
                  Simplificar la operación empresarial mediante un ERP que realmente se adapta a tu negocio.
                </p>
                <p className="text-lg text-gray-600 mt-4">
                  No queremos que las empresas cambien su forma de trabajar para usar un software: queremos que el software se adapte a ellas.
                </p>
              </div>

              <div className="bg-gray-50 p-10 rounded-lg">
                <Heart className="w-12 h-12 text-green-600 mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Nuestro Propósito
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed">
                  Crear tecnología simple, potente y accesible, que automatice procesos, reduzca costos, mejore la experiencia del cliente y permita a las empresas enfocarse en lo que realmente importa: crecer.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Today */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <Building2 className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-gray-900 mb-12">
              Nuestros Logros: Cloudbase Hoy
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 text-left mb-12">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">10+ años de experiencia</p>
                  <p className="text-gray-600">En desarrollo y automatización empresarial</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">ERP modular adoptado</p>
                  <p className="text-gray-600">Por cientos de empresas en LATAM</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">Operación en 5 países</p>
                  <p className="text-gray-600">Chile, Perú, Colombia, Argentina y Brasil</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">Integraciones robustas</p>
                  <p className="text-gray-600">Con herramientas líderes del mercado</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">Equipo dedicado</p>
                  <p className="text-gray-600">Soporte, desarrollo e innovación continua</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">Plataforma escalable</p>
                  <p className="text-gray-600">Moderna, segura y lista para crecer</p>
                </div>
              </div>
            </div>

            <div className="text-3xl font-bold text-gray-900 mb-6">
              Cloudbase. El ERP modular que crece contigo.
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para transformar tu empresa?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Descubre cómo Cloudbase puede ayudarte a automatizar, crecer y escalar.
            </p>
            <Button
              size="lg"
              className="bg-white text-green-600 hover:bg-gray-100 px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
              onClick={() => window.location.href = '/demo'}
            >
              Agenda una Demo
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default AboutPage;